/**
 * Script para capturar logs do servidor
 * 
 * Uso:
 * 1. Abra este arquivo no navegador: http://localhost:3000/debug/capture-logs
 * 2. Clique em "Limpar Logs"
 * 3. Vá para "Editar Matriz" e clique em "Editar Matriz"
 * 4. Aguarde o timeout (30s)
 * 5. Volte para esta página
 * 6. Clique em "Capturar Logs"
 * 7. Copie o JSON e compartilhe
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { trpc } from '@/lib/trpc';
import { Textarea } from '@/components/ui/textarea';

export default function DebugCaptureLogsScript() {
  const [capturedLogs, setCapturedLogs] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  // Queries
  const timingQuery = trpc.debug.logs.getOperationTiming.useQuery(
    { operationName: 'ensureClassificationDraft' },
    { enabled: false }
  );

  const handleClearLogs = async () => {
    try {
      setIsLoading(true);
      // Nota: clearDebugLogs é uma mutation, mas pode não estar exposta
      // Por enquanto, apenas refetch para pegar logs atualizados
      alert('Logs não podem ser limpos via UI. Reinicie o servidor para limpar.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptureLogs = async () => {
    try {
      setIsLoading(true);
      
      // Buscar timing detalhado
      const result = await timingQuery.refetch();
      
      if (result.data) {
        const formattedData = JSON.stringify(result.data, null, 2);
        setCapturedLogs(formattedData);
        
        // Também copiar para clipboard
        navigator.clipboard.writeText(formattedData);
        alert('✅ Logs capturados e copiados para clipboard!');
      }
    } catch (error) {
      console.error('Erro ao capturar logs:', error);
      alert('❌ Erro ao capturar logs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(capturedLogs);
    alert('✅ Copiado para clipboard!');
  };

  const handleDownloadJSON = () => {
    const element = document.createElement('a');
    const file = new Blob([capturedLogs], { type: 'application/json' });
    element.href = URL.createObjectURL(file);
    element.download = `debug-logs-${Date.now()}.json`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Capturar Logs de Debug</h1>
        <p className="text-gray-500">Script para coletar timings de ensureClassificationDraft()</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Instruções</CardTitle>
          <CardDescription>Siga os passos para capturar os logs</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 text-sm">
            <p><strong>Passo 1:</strong> Clique em "Limpar Logs" (reinicia servidor)</p>
            <p><strong>Passo 2:</strong> Vá para "Motor de Análise" → "Matriz de Classificação"</p>
            <p><strong>Passo 3:</strong> Clique em "Editar Matriz"</p>
            <p><strong>Passo 4:</strong> Aguarde o timeout (30s) ou sucesso</p>
            <p><strong>Passo 5:</strong> Volte para esta página</p>
            <p><strong>Passo 6:</strong> Clique em "Capturar Logs"</p>
            <p><strong>Passo 7:</strong> Copie o JSON e compartilhe</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ações</CardTitle>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Button 
            onClick={handleClearLogs}
            variant="outline"
            disabled={isLoading}
          >
            🗑️ Limpar Logs (Reinicia Servidor)
          </Button>
          <Button 
            onClick={handleCaptureLogs}
            disabled={isLoading}
          >
            📸 Capturar Logs
          </Button>
        </CardContent>
      </Card>

      {capturedLogs && (
        <Card>
          <CardHeader>
            <CardTitle>Logs Capturados</CardTitle>
            <CardDescription>Timings de cada ETAPA</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Button onClick={handleCopyToClipboard} variant="outline">
                📋 Copiar para Clipboard
              </Button>
              <Button onClick={handleDownloadJSON} variant="outline">
                💾 Download JSON
              </Button>
            </div>

            <Textarea
              value={capturedLogs}
              readOnly
              className="font-mono text-xs h-96"
            />

            {/* Análise rápida */}
            {(() => {
              try {
                const data = JSON.parse(capturedLogs);
                const stageTiming = data.stageTimings || {};
                
                return (
                  <div className="space-y-2 bg-gray-100 p-4 rounded">
                    <p className="font-bold">📊 Análise Rápida:</p>
                    <p>Tempo Total: <strong>{data.totalTime}ms</strong></p>
                    <p>Status: <strong>{data.hasError ? '❌ ERRO' : '✅ SUCESSO'}</strong></p>
                    
                    {Object.entries(stageTiming).length > 0 && (
                      <div className="space-y-1 mt-2">
                        <p className="font-bold">Timing por ETAPA:</p>
                        {Object.entries(stageTiming).map(([stage, time]) => (
                          <div key={stage} className="flex justify-between text-sm">
                            <span>{stage}</span>
                            <span className={`font-bold ${(time as number) > 10000 ? 'text-red-600' : 'text-green-600'}`}>
                              {time}ms
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {data.errorMessage && (
                      <div className="mt-2 bg-red-100 p-2 rounded">
                        <p className="text-sm text-red-700"><strong>Erro:</strong> {data.errorMessage}</p>
                      </div>
                    )}
                  </div>
                );
              } catch (e) {
                return null;
              }
            })()}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>O que Procurar nos Logs</CardTitle>
          <CardDescription>Identifique qual ETAPA está demorando {'>'} 30s</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p><strong>ETAPA 1 (acquireLock):</strong> Adquirir lock de concorrência - esperado: 10-100ms</p>
          <p><strong>ETAPA 2 (syncSeverities):</strong> Sincronizar severidades - esperado: 100-5000ms</p>
          <p><strong>ETAPA 3 (fetchVersion):</strong> Carregar versão - esperado: 10-100ms</p>
          <p><strong>ETAPA 4 (fetchProbabilities):</strong> Carregar probabilidades - esperado: 50-500ms</p>
          <p><strong>ETAPA 5 (fetchSeverities):</strong> Carregar severidades - esperado: 50-500ms</p>
          <p><strong>ETAPA 6 (fetchCells):</strong> Carregar células - esperado: 100-10000ms</p>
          
          <p className="mt-4"><strong>Se alguma ETAPA {'>'} 10s:</strong> Há um gargalo naquela operação</p>
          <p><strong>Se tempo total {'>'} 30s:</strong> Timeout dispara antes de completar</p>
        </CardContent>
      </Card>
    </div>
  );
}
